import React from 'react';
import { motion } from 'framer-motion';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { Calendar, Users, Target, Phone, MapPin, Facebook, Info } from 'lucide-react';
import { PlanYourVisitCTA } from '../components/PlanYourVisitCTA';
import { PHONE, PHONE_TEL, GOOGLE_MAPS_URL, FACEBOOK_URL } from '../constants/business';
export function Events() {
  useDocumentMeta('Events & Demo Shoots | Kjergaard Sports', 'Stay updated on events, store updates, and the Buffalo Ridge Demo Shoot hosted by Kjergaard Sports in Lake Benton, Minnesota.');
  return <div data-id="element-258">
      {/* HERO */}
      <section className="relative flex items-center min-h-[60vh] lg:min-h-[480px] py-20 lg:py-24 overflow-hidden" data-id="element-259">
        <div className="absolute inset-0 z-0" data-id="element-260">
          <img src="https://cdn.magicpatterns.com/uploads/uUknJmVfAFGtW8fiJmX2Gd/events1.jpg" alt="Buffalo Ridge Demo Shoot — Benelli, Browning, and Winchester tents with firearms displayed on tables" className="w-full h-full object-cover object-center" data-id="element-261" />
          {/* Lighter left-to-right gradient so the photo reads through */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/85 via-brand-charcoal/55 to-brand-charcoal/25" data-id="element-262"></div>
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-brand-charcoal/70 to-transparent" data-id="element-263"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-264">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="max-w-3xl" data-id="element-265">
            <div className="flex items-center space-x-3 mb-5" data-id="element-266">
              <div className="h-px w-10 bg-brand-leather" data-id="element-267"></div>
              <span className="text-brand-bone uppercase tracking-widest text-xs font-semibold drop-shadow-md" data-id="element-268">
                Kjergaard Sports
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-offwhite leading-tight mb-5 drop-shadow-lg" data-id="element-269">
              Events &amp; Demo Shoots
            </h1>

            <p className="text-base md:text-lg text-brand-cream leading-relaxed mb-8 max-w-2xl drop-shadow-md" data-id="element-270">
              Classes, store updates, demo shoots, and community events for
              hunters and shooting sports customers in Lake Benton, Minnesota.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-271">
              <a href={PHONE_TEL} className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors flex items-center justify-center" data-id="element-272">
                <Phone className="w-5 h-5 mr-2" data-id="element-273" />
                Call the Store
              </a>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="border-2 border-brand-bone hover:border-brand-leather hover:bg-brand-leather/10 text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors flex items-center justify-center" data-id="element-274">
                <Facebook className="w-5 h-5 mr-2" data-id="element-275" />
                Follow on Facebook
              </a>
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="bg-brand-offwhite/10 hover:bg-brand-offwhite/20 text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors flex items-center justify-center backdrop-blur-sm border border-brand-offwhite/20" data-id="element-276">
                <MapPin className="w-5 h-5 mr-2" data-id="element-277" />
                Get Directions
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* COMMUNITY & CONNECTION + EVENT CARDS */}
      <section className="py-20 bg-brand-offwhite" data-id="element-278">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-279">
          {/* Community & Connection */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20" data-id="element-280">
            <div data-id="element-281">
              <div className="flex items-center space-x-3 mb-5" data-id="element-282">
                <div className="h-px w-10 bg-brand-leather" data-id="element-283"></div>
                <span className="text-brand-hunter uppercase tracking-widest text-xs font-semibold" data-id="element-284">
                  Events, updates, and in-store support
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6 leading-tight" data-id="element-285">
                Community &amp; Connection
              </h2>
              <p className="text-lg text-brand-charcoal/80 mb-5 leading-relaxed" data-id="element-286">
                Kjergaard Sports is more than a store; it's a gathering place
                for the local shooting and hunting community. Throughout the
                year, we host events designed to bring enthusiasts together,
                showcase new products, and promote safe, responsible firearm
                use.
              </p>
              <p className="text-base text-brand-charcoal/75 leading-relaxed" data-id="element-287">
                Demo shoots, class updates, manufacturer days, and seasonal
                announcements all happen in person — so make the trip, call
                ahead, or follow along on Facebook for the latest.
              </p>
            </div>

            <div className="relative" data-id="element-288">
              <div className="absolute inset-0 bg-brand-sage translate-x-4 translate-y-4 rounded-sm" data-id="element-289"></div>
              <img src="https://cdn.magicpatterns.com/uploads/nBYgfXMrjN6a9xShzw47uG/events.jpg" alt="Browning and Benelli manufacturer displays set up at a Kjergaard Sports event" className="relative z-10 w-full h-72 md:h-96 rounded-sm shadow-lg object-cover" data-id="element-290" />
            </div>
          </div>

          {/* Event cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-291">
            <div className="bg-brand-bone p-10 rounded-sm border border-brand-cream flex flex-col h-full" data-id="element-292">
              <Target className="w-10 h-10 text-brand-hunter mb-6" data-id="element-293" />
              <h3 className="text-2xl font-serif text-brand-charcoal mb-4" data-id="element-294">
                Buffalo Ridge Demo Shoot
              </h3>
              <p className="text-brand-charcoal/80 leading-relaxed mb-6 flex-grow" data-id="element-295">
                Our annual Buffalo Ridge Demo Shoot is a unique opportunity to
                test and experience a variety of firearms in a safe, supervised
                environment. It's a great way to compare options, learn more,
                and connect with other shooting sports enthusiasts and industry
                representatives.
              </p>
            </div>

            <div className="bg-brand-bone p-10 rounded-sm border border-brand-cream flex flex-col h-full" data-id="element-296">
              <Users className="w-10 h-10 text-brand-hunter mb-6" data-id="element-297" />
              <h3 className="text-2xl font-serif text-brand-charcoal mb-4" data-id="element-298">
                Store Events &amp; Updates
              </h3>
              <p className="text-brand-charcoal/80 leading-relaxed mb-6 flex-grow" data-id="element-299">
                Throughout the year, we host various store events, manufacturer
                days, class updates, and product demos where applicable. These
                events offer a chance to speak directly with experts and see new
                gear firsthand.
              </p>
            </div>
          </div>

          {/* Supporting image strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-16" data-id="element-300">
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-301">
              <img src="https://cdn.magicpatterns.com/uploads/4MjKD2zi9iiVqWYu7BdiBh/events2.jpg" alt="Large event tent at a Kjergaard Sports community gathering" className="w-full h-44 md:h-52 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-302" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-303">
              <img src="https://cdn.magicpatterns.com/uploads/pW5HFrMebncV4g2gQkm2kR/events3.jpg" alt="Customers browsing firearms inside the demo shoot tent at Kjergaard Sports" className="w-full h-44 md:h-52 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-304" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-305">
              <img src="https://cdn.magicpatterns.com/uploads/vXFtU1J5XUHFpFDTrQ4FEp/events5.jpg" alt="Beretta, Nosler, and BPI Outdoors manufacturer booths at a Kjergaard Sports demo event" className="w-full h-44 md:h-52 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-306" />
            </div>
          </div>

          {/* Schedule CTA */}
          <div className="bg-brand-hunter text-brand-offwhite p-10 rounded-sm text-center max-w-4xl mx-auto" data-id="element-307">
            <Calendar className="w-12 h-12 text-brand-leather mx-auto mb-6" data-id="element-308" />
            <h2 className="text-2xl md:text-3xl font-serif mb-4" data-id="element-309">
              Current Event Schedule
            </h2>
            <p className="text-lg text-brand-offwhite/85 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-310">
              Event dates and details change frequently. Call the store or
              follow Kjergaard Sports on Facebook for the most up-to-date
              information on upcoming events.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-311">
              <a href={PHONE_TEL} className="bg-brand-leather hover:bg-brand-wood text-brand-offwhite px-8 py-3 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-312">
                <Phone className="w-5 h-5 mr-2" data-id="element-313" />
                Call {PHONE}
              </a>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="border border-brand-offwhite/40 hover:border-brand-offwhite text-brand-offwhite px-8 py-3 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-314">
                <Facebook className="w-5 h-5 mr-2" data-id="element-315" />
                Follow on Facebook
              </a>
            </div>

            <div className="mt-8 pt-6 border-t border-brand-offwhite/15 flex items-start text-left max-w-2xl mx-auto" data-id="element-316">
              <Info className="w-5 h-5 text-brand-leather mr-3 flex-shrink-0 mt-0.5" data-id="element-317" />
              <p className="text-brand-offwhite/75 text-sm leading-relaxed" data-id="element-318">
                This website is for business information only. Kjergaard Sports
                does not conduct sales directly through this website. Event
                schedules and class dates change — please call or follow
                Facebook for current updates.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PlanYourVisitCTA data-id="element-319" />
    </div>;
}